import React, { useEffect, useState } from 'react';
import { Modal as BootstrapModal, Col, FormControl, FormGroup, Grid, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AccountService from '../../api/AccountService';
import { WithAuth } from '../Authorization/WithAuth';
import Card from '../Cards/Card';
import { CustomSelect } from '../CustomControls/CustomSelect';
import { ErrorMessage } from '../Notifications/Notifications';
import { PersonalData } from '../Person/PersonalData';
import { Actions } from './Actions';
import useTwoFactorIsEnabled from './useTwoFactorEnabled';

export const UserSettingsForm = props => {
    const {
        isSubmitting,
        onChangeRole,
        onEnableUser,
        onDisableUser,
        onResendEmail,
        onNewPassword,
        user,
        roles,
        values,
        setFieldValue,
        setFieldTouched,
        handleChange,
        t,
    } = props;
    
    const [showMfaModal, setShowMfaModal] = useState(false);
    const [showDesactivationModal, setShowDesactivationModal] = useState(false);
    const currentUser = useSelector(state => state.authentication.user);
    const [twoFactiorEnabled, twoFactorConfigIsLoading, reloadTwoFactor] = useTwoFactorIsEnabled({ email: user?.userName });
    let history = useHistory();

    async function onCloseMfaModal() {
        await reloadTwoFactor();
        setShowMfaModal(false);
    }

    async function onCloseDesactivationModal() {
        await reloadTwoFactor();
        setShowDesactivationModal(false);
    }

    const isCurrentUser = currentUser.sub == user.userId;
    const DisableButton = () => <button className="btn btn-success" type="button" disabled={isSubmitting} onClick={() => setShowDesactivationModal(true)}> {t('buttons.disable')}</button>;

    return (
        <Grid fluid>
            <Row className="display-flex">
                <Col md={6}>
                    <PersonalData data={values} />
                </Col>
                <WithAuth requiredPermission={["Users.Create"]}>
                    <Col md={6}>
                        {
                            values.active ?
                                <Card
                                    title={t("security.users.disableUser")}
                                    content={
                                        <button className="btn btn-danger" type="button" disabled={isSubmitting} onClick={onDisableUser}>
                                            {t("buttons.disableUser")}
                                        </button>
                                    }
                                />
                                :
                                <Card
                                    title={t("security.users.enableUser")}
                                    content={
                                        <button className="btn btn-success" type="button" disabled={isSubmitting} onClick={onEnableUser}>
                                            {t("buttons.enableUser")}
                                        </button>
                                    }
                                />
                        }
                    </Col>
                </WithAuth>
            </Row>
            <Row>
                <Col md={12} lg={2}>
                    <Actions
                        onRedirectTo={() => history.goBack()}
                    />
                </Col>
                <Col md={10}>
                    <Card
                        title=""
                        content={
                            <FormGroup>
                                <WithAuth requiredPermission={["Users.Create"]}>
                                    <Grid fluid>
                                        <Row>
                                            <Col sm={6}>
                                                <div className="card-label">{t("security.users.changeRole")}</div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={6}>
                                                <CustomSelect
                                                    defaultValue={roles && roles.filter(x => x && x.name === values.role)}
                                                    name={`role`}
                                                    placeholder={t("commons.select")}
                                                    onChange={setFieldValue}
                                                    onBlur={setFieldTouched}
                                                    options={roles}
                                                    getOptionLabel={x => x.name}
                                                />
                                            </Col>
                                            <Col sm={6}>
                                                <button className="btn btn-success" type="button" disabled={isSubmitting} onClick={() => onChangeRole(values.role?.id)}>
                                                    {t("buttons.save")}
                                                </button>
                                            </Col>
                                        </Row>
                                    </Grid>
                                    <Grid fluid style={{ marginTop: '40px' }}>
                                        <Row>
                                            <Col sm={6}>
                                                <div className="card-label">{t("security.users.resendEmail")}</div>
                                            </Col>
                                            <Col sm={6}>
                                                <button className="btn btn-success" type="button" disabled={isSubmitting} onClick={() => onResendEmail(values.role?.id)}>
                                                    {t("security.users.resendEmail_button")}
                                                </button>
                                            </Col>
                                        </Row>
                                    </Grid>

                                    <Grid fluid>
                                        <Row>
                                            <Col sm={6}>
                                                <div className="card-label">{t("security.users.changePassword")}</div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={6}>
                                                <FormControl autoFocus type="text" name={`newpassword`} value={values.newpassword || ''} onChange={handleChange} autoComplete="off" />
                                            </Col>
                                            <Col sm={6}>
                                                <button className="btn btn-success" type="button" disabled={isSubmitting} onClick={() => onNewPassword(values.newpassword)}>
                                                    {t("security.users.changePassword_button")}
                                                </button>
                                            </Col>
                                        </Row>
                                    </Grid>
                                </WithAuth>

                                {/* Config MFA */}
                                {
                                    !twoFactorConfigIsLoading &&
                                    <Grid fluid style={{ marginTop: '40px' }}>
                                        <Row>
                                            <Col sm={6}>
                                                <div className="card-label">{t('security.users.configureAuthenticator')}</div>
                                            </Col>
                                            <Col sm={6}>
                                                {
                                                    !twoFactiorEnabled &&
                                                    (<button className="btn btn-success" type="button" disabled={isSubmitting || currentUser.sub !== user.userId} onClick={() => { setShowMfaModal(true) }}>
                                                        {t('buttons.enable')}
                                                    </button>)
                                                    || (isCurrentUser
                                                        ? <DisableButton></DisableButton>
                                                        : <WithAuth requiredPermission={["Users.Create"]}><DisableButton></DisableButton></WithAuth>)
                                                }
                                            </Col>
                                            {showMfaModal && <MfaRegistrationModal onClose={onCloseMfaModal} />}
                                            {showDesactivationModal && <AuthenticatorDesactivationModal onClose={onCloseDesactivationModal} currentUser={user} isCurrentUser={isCurrentUser} />}
                                        </Row>
                                    </Grid>
                                }
                            </FormGroup>
                        }
                    />
                </Col>
            </Row>
        </Grid>
    );
};


function AuthenticatorDesactivationModal({ onClose, currentUser, isCurrentUser }) {
    const { t } = useTranslation();

    async function onSubmitMfaSetup(e) {
        e.preventDefault();
        try {
            const response = isCurrentUser == true
                ? await AccountService.DisableAuthenticator()
                : await AccountService.DisableAuthenticatorByEmail({ email: currentUser.userName });

            if (response.Error !== "Bad Request") {
                onCloseModal();
            } else {
                ErrorMessage(t('security.users.authenticatorCodeInvalid'));
            }
        } catch (error) {
            ErrorMessage(t('errors.serverError'));
        }
    }

    function onCloseModal() {
        if (onClose) onClose();
    }

    return (<BootstrapModal show={true} onHide={onCloseModal} bsSize="small" backdrop="static">
        <BootstrapModal.Header>
            <BootstrapModal.Title>{t('security.users.disableAuthenticator')}</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
            <div>{t('security.users.disableAuthenticatorTooltip')}</div>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
            <button className="btn btn-default" type="button" onClick={onCloseModal}>
                {t("buttons.cancel")}
            </button>
            <button className="btn btn-success" type="button" disabled={false} onClick={onSubmitMfaSetup}>
                {t("buttons.apply")}
            </button>
        </BootstrapModal.Footer>
    </BootstrapModal>)
}

function MfaRegistrationModal({ onClose }) {
    const { t } = useTranslation();
    const [qrImage, setQrImage] = useState(null);
    const [code, setCode] = useState(null);

    useEffect(() => {
        onFetchQrConfiguration()
    }, [])

    async function onFetchQrConfiguration() {
        const response = await AccountService.GenerateAuthenticatorQrRegistration()
        setQrImage(response.qrCodeImage);
    }

    async function onSubmitMfaSetup(e) {
        e.preventDefault();
        try {

            const response = await AccountService.PostEnableAuthenticator({ code: code });

            if (response.Error !== "Bad Request") {
                onCloseModal();
            } else {
                ErrorMessage(t('security.users.authenticatorCodeInvalid'));
            }
        } catch (error) {
            ErrorMessage(t('errors.serverError'));
        }
    }

    function onCloseModal() {
        if (onClose) onClose();
    }

    return (<BootstrapModal show={true} onHide={onCloseModal} bsSize="small" backdrop="static" onKeyDown={(e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
        }
    }}>
        <BootstrapModal.Header>
            <BootstrapModal.Title>{t('security.users.configureAuthenticator')}</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
            <div className='alert alert-info'>
                {t('security.users.authenticatorTooltip')}
            </div>
            {qrImage != null &&
                <form>
                    <div></div>
                    <div>
                        <img width={"100%"} src={qrImage} alt="QR Code for MFA" />
                    </div>
                    <div>
                        <FormGroup>
                            <label>
                                {t('security.users.authenticatorCode')}
                            </label>
                            <FormControl
                                autoFocus
                                type="text"
                                name="mfaCode"
                                value={code || ''}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    if (value.length <= 6) {
                                        setCode(value);
                                    }
                                }}
                                autoComplete="off"
                                style={{ textAlign: "center" }}
                                maxLength={6}
                            />
                        </FormGroup>
                    </div>
                </form>}

        </BootstrapModal.Body>
        <BootstrapModal.Footer>
            <button className="btn btn-default" type="button" onClick={onCloseModal}>
                {t("buttons.cancel")}
            </button>
            <button className="btn btn-success" type="button" disabled={code?.length < 6} onClick={onSubmitMfaSetup}>
                {t("buttons.apply")}
            </button>
        </BootstrapModal.Footer>
    </BootstrapModal>)
}